import React, { useState, useEffect } from 'react';
import { apiService } from '../../../services/api';
import './DeleteDatasetModal.css';

const DeleteDatasetModal = ({ dataset, onClose, onConfirm, onShowNotification }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const fetchPreview = async () => {
      try {
        const data = await apiService.deleteDatasetPreview(dataset.id);
        setPreview(data);
      } catch (error) {
        onShowNotification('Failed to load delete preview. Please try again.', 'error');
        onClose();
      } finally {
        setLoading(false);
      }
    };
    fetchPreview();
  }, [dataset.id, onClose, onShowNotification]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onConfirm();
    } catch {
      setDeleting(false);
    }
  };

  return (
    <div className="delete-dataset-modal-overlay" onClick={onClose}>
      <div className="delete-dataset-modal" onClick={e => e.stopPropagation()}>
        <h2>Delete Dataset</h2>
        <p className="delete-dataset-modal-name">{dataset.name}</p>

        {loading ? (
          <div className="delete-dataset-modal-loading">
            <div className="delete-dataset-modal-spinner" />
            <p>Loading preview...</p>
          </div>
        ) : preview ? (
          <>
            <div className="delete-dataset-modal-stats">
              <div className="delete-dataset-modal-stat">
                <span className="delete-dataset-modal-stat-value">{preview.file_count}</span>
                <span className="delete-dataset-modal-stat-label">Files</span>
              </div>
              <div className="delete-dataset-modal-stat">
                <span className="delete-dataset-modal-stat-value">{preview.extraction_job_count}</span>
                <span className="delete-dataset-modal-stat-label">Extraction Jobs</span>
              </div>
            </div>

            {(preview.widget_count_yours > 0 || preview.widget_count_others > 0) && (
              // Counted rather than listed. A widget keeps its dataset id after the
              // dataset goes and fails on every load afterwards, so these break the
              // moment this is confirmed. Other people's are called out separately
              // because they are the ones you cannot repoint and they will not be
              // told -- someone just opens a dashboard and finds a broken card.
              <div className="delete-dataset-modal-widgets">
                <p className="delete-dataset-modal-widgets-title">
                  <span className="delete-dataset-modal-automation-icon">&#9888;</span>
                  {' '}Insights widgets built on this dataset will stop working:
                </p>
                <ul>
                  {preview.widget_count_yours > 0 && (
                    <li>
                      {preview.widget_count_yours}
                      {preview.widget_count_yours === 1 ? ' widget of yours' : ' widgets of yours'}
                    </li>
                  )}
                  {preview.widget_count_others > 0 && (
                    <li>
                      {preview.widget_count_others}
                      {preview.widget_count_others === 1
                        ? " widget belonging to someone else"
                        : " widgets belonging to other people"}
                    </li>
                  )}
                </ul>
              </div>
            )}

            {preview.automations && preview.automations.length > 0 && (
              <div className="delete-dataset-modal-automations">
                <p className="delete-dataset-modal-automations-title">
                  The following automations reference this dataset and will need to be updated or removed separately:
                </p>
                <ul>
                  {preview.automations.map((automation) => (
                    <li key={automation.automation_id}>
                      <span className="delete-dataset-modal-automation-icon">&#9888;</span>
                      <div>
                        <span className="delete-dataset-modal-automation-name">
                          {automation.automation_name}
                        </span>
                        {automation.created_by_email && (
                          <span className="delete-dataset-modal-automation-email">
                            {automation.created_by_email}
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p className="delete-dataset-modal-warning">
              This will permanently remove this dataset and all its traces from the organization,
              including files and extraction results.
            </p>
          </>
        ) : null}

        <div className="delete-dataset-modal-actions">
          <button
            className="delete-dataset-modal-cancel"
            onClick={onClose}
            disabled={deleting}
          >
            Cancel
          </button>
          <button
            className="delete-dataset-modal-delete"
            onClick={handleDelete}
            disabled={loading || deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteDatasetModal;
